import { FC, memo, useEffect, useMemo, useState } from "react";
import { formatFileSize } from "helpers/utils";
import {
  RunFileSnapshot,
  RunKey,
  UploadFileStatus,
  useUploadRun,
} from "./upload/uploadEngine";

// Rows drawn at once; the rest is one "+N more" line. The cap is about redraw cost at four
// publishes a second, not about the DOM.
const MAX_ROWS = 50;

// Order when the list is longer than the window: what needs a decision first, then what is
// moving, then the queue. Finished rows sink, so the window follows the run instead of showing
// the first 50 files, all done, for the next hour.
const STATUS_ORDER: { [status in UploadFileStatus]: number } = {
  error: 0,
  uploading: 1,
  completing: 2,
  queued: 3,
  done: 4,
  canceled: 5,
};

const STATUS_ICON: { [status in UploadFileStatus]: string } = {
  queued: "icon-doc text-gray-400",
  uploading: "inline-block animate-spin icon-spin4 text-[#2283C5]",
  completing: "inline-block animate-spin icon-spin4 text-[#2283C5]",
  done: "icon-ok text-[#629B58]",
  error: "icon-cancel-circled text-red-600",
  canceled: "icon-cancel text-gray-400",
};

const STATUS_TITLE: { [status in UploadFileStatus]: string } = {
  queued: "Waiting",
  uploading: "Uploading",
  completing: "Finalizing on the server",
  done: "Uploaded",
  error: "Failed",
  canceled: "Canceled",
};

const AUTH_EXPIRED_TEXT =
  "Your session has expired. Sign in again; this upload has to be started over.";

const EMPTY_FILES: RunFileSnapshot[] = [];

const formatEta = (seconds: number): string => {
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
};

const FileRow = memo(function FileRow({
  file,
  bannerError,
}: {
  file: RunFileSnapshot;
  // The message the banner above already carries; repeating it on every row says nothing new
  bannerError: string | null;
}) {
  const label = file.path ? `${file.path}/${file.name}` : file.name;
  const percent = file.size
    ? Math.min(100, Math.floor((file.sent / file.size) * 100))
    : 100;
  const error =
    file.status === "error" && file.error && file.error !== bannerError
      ? file.error
      : null;

  return (
    <div className="py-[2px] text-[13px] leading-5 text-[#393939]">
      <div className="flex items-center gap-2">
        <i
          className={`${STATUS_ICON[file.status]} flex-shrink-0`}
          title={STATUS_TITLE[file.status]}
        />
        <span className="min-w-0 flex-1 truncate" title={label}>
          {label}
        </span>
        {file.status === "uploading" && (
          <span className="text-xs text-gray-500 tabular-nums">{percent}%</span>
        )}
        <span className="whitespace-nowrap text-xs text-gray-500 tabular-nums">
          {formatFileSize(file.size)}
        </span>
      </div>
      {!!error && (
        <div className="break-words pl-6 text-xs text-red-600">{error}</div>
      )}
      {!!file.note && (
        <div className="break-words pl-6 text-xs text-gray-500">{file.note}</div>
      )}
    </div>
  );
});

/**
 * Progress of one direct-to-S3 run, i.e. of one file card.
 *
 * Subscribes to the engine store itself and gets nothing but the run key from the card, so the
 * four publishes a second redraw this leaf and not the whole Misc tab. Renders nothing when the
 * key has no run, which keeps the card unchanged for everyone who never uploads.
 */
const QuoteUploadPanel: FC<{
  runKey: RunKey;
  onCancel: () => void;
  onRetry: () => void;
}> = ({ runKey, onCancel, onRetry }) => {
  const snapshot = useUploadRun(runKey);
  const state = snapshot ? snapshot.state : null;
  const isActive = state === "running" || state === "paused";
  const files = snapshot ? snapshot.files : EMPTY_FILES;

  // "Hide" only puts a finished run away; the next run under this key brings the panel back
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (isActive) setHidden(false);
  }, [isActive]);

  const { rows, moreCount, canceledCount, sharedError, noteCount, sharedNote } =
    useMemo(() => {
    let canceled = 0;
    // The same text on every failed row (CORS refused, server unreachable, session budget) is
    // one problem: said once above the list rather than fifty times below it
    let shared: string | null | undefined;
    // Same for the server's remarks on saved files: one line with the count above the list,
    // the rows keep theirs so the files concerned can be told apart
    let notes = 0;
    let sharedRemark: string | null | undefined;
    files.forEach((file) => {
      if (file.status === "canceled") canceled++;
      if (file.note) {
        notes++;
        if (sharedRemark === undefined) sharedRemark = file.note;
        else if (sharedRemark !== file.note) sharedRemark = null;
      }
      if (file.status !== "error") return;
      const error = file.error || null;
      if (shared === undefined) shared = error;
      else if (shared !== error) shared = null;
    });
    const sharedText = shared || null;
    const sharedRemarkText = sharedRemark || null;
    if (files.length <= MAX_ROWS) {
      return {
        rows: files,
        moreCount: 0,
        canceledCount: canceled,
        sharedError: sharedText,
        noteCount: notes,
        sharedNote: sharedRemarkText,
      };
    }
    const ordered = files
      .map((file, index) => ({ file, index }))
      .sort(
        (a, b) =>
          STATUS_ORDER[a.file.status] - STATUS_ORDER[b.file.status] ||
          a.index - b.index,
      );
    return {
      rows: ordered.slice(0, MAX_ROWS).map((item) => item.file),
      moreCount: files.length - MAX_ROWS,
      canceledCount: canceled,
      sharedError: sharedText,
      noteCount: notes,
      sharedNote: sharedRemarkText,
    };
  }, [files]);

  if (!snapshot || hidden) return null;

  const total = files.length;
  const percent = snapshot.totalBytes
    ? Math.min(100, (snapshot.sentBytes / snapshot.totalBytes) * 100)
    : snapshot.doneCount === total
    ? 100
    : 0;
  const isDone = state === "done";
  const isExpired = state === "authExpired";
  const hasErrors = snapshot.errorCount > 0;
  // Files only become "canceled" through Cancel, after which the engine ignores a retry
  const canRetry = isDone && hasErrors && canceledCount === 0;
  const isSuccess = isDone && !hasErrors && !canceledCount;

  let title: string;
  if (state === "paused") title = "Paused: waiting for the network connection…";
  else if (isExpired) title = "Upload stopped: your session has expired";
  else if (isDone && canceledCount) title = "Upload canceled";
  else if (isDone && hasErrors) title = "Upload finished with errors";
  else if (isDone) title = "Upload complete";
  else title = "Uploading…";

  const metaParts: string[] = [
    `${formatFileSize(snapshot.sentBytes)} of ${formatFileSize(
      snapshot.totalBytes,
    )}`,
  ];
  if (state === "running" && snapshot.bytesPerSecond > 0) {
    metaParts.push(`${formatFileSize(snapshot.bytesPerSecond)}/s`);
  }
  if (state === "running" && snapshot.etaSeconds !== null) {
    metaParts.push(`about ${formatEta(snapshot.etaSeconds)} left`);
  }
  if (isActive && hasErrors) {
    metaParts.push(`${snapshot.errorCount} failed so far`);
  }

  let banner: string | null = null;
  if (isExpired) {
    banner = sharedError || AUTH_EXPIRED_TEXT;
  } else if (isDone && hasErrors) {
    banner =
      `${snapshot.errorCount} file${snapshot.errorCount > 1 ? "s" : ""} failed` +
      (sharedError ? `: ${sharedError}` : "; the reasons are on the rows below.");
  }

  const barColor =
    isExpired || (isDone && hasErrors)
      ? "bg-red-500"
      : state === "paused"
      ? "bg-[#f59942]"
      : isDone
      ? "bg-[#87B87F]"
      : "bg-[#2283C5]";
  const titleIcon = isActive
    ? "inline-block animate-spin icon-spin4"
    : isSuccess
    ? "icon-ok text-[#629B58]"
    : "icon-upload";

  return (
    <div className="mb-2 rounded border border-[#d9dde3] bg-[#f7f8fa] px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 text-[13px] font-semibold text-[#393939]">
          <i className={`${titleIcon} pr-1`} />
          {title}
          <span className="pl-2 font-normal text-gray-500">
            {snapshot.doneCount} of {total} file{total === 1 ? "" : "s"} ·{" "}
            {Math.floor(percent)}%
          </span>
        </div>
        <span className="flex flex-shrink-0 items-center gap-1">
          {isActive && (
            <button
              type="button"
              className="btn btn-small btn-warning"
              onClick={onCancel}
            >
              <i className="icon-cancel pr-1" />
              Cancel
            </button>
          )}
          {canRetry && (
            <button
              type="button"
              className="btn btn-small btn-success"
              onClick={onRetry}
            >
              <i className="icon-arrows-cw pr-1" />
              Retry failed
            </button>
          )}
          {!isActive && (
            <button
              type="button"
              className="btn btn-small"
              title={
                canRetry
                  ? "Hide this report and give up on the failed files"
                  : "Hide this report"
              }
              onClick={() => {
                // Giving up on the failed files releases their S3 side now instead of
                // leaving it to the bucket lifecycle rules
                if (canRetry) onCancel();
                setHidden(true);
              }}
            >
              Hide
            </button>
          )}
        </span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className={`h-full rounded-full transition-all duration-200 ease-out ${barColor}`}
          style={{ width: `${Math.round(percent * 10) / 10}%` }}
        />
      </div>
      <div className="mt-1 text-xs text-gray-500">{metaParts.join(" · ")}</div>
      {!!banner && (
        <div className="mt-2 rounded border border-[#f0c9c9] bg-[#fdf3f3] px-3 py-2 text-sm text-[#7a2f2f]">
          {banner}
        </div>
      )}
      {noteCount > 0 && (
        <div className="mt-2 rounded border border-[#e6dcb8] bg-[#fdf8e7] px-3 py-2 text-sm text-[#6b5a1e]">
          {`${noteCount} file${noteCount > 1 ? "s" : ""} saved with a note`}
          {sharedNote ? `: ${sharedNote}` : "; see the rows below."}
        </div>
      )}
      {!!rows.length && (
        <div className="mt-2 border-t border-[#e5e7eb] pt-1">
          {rows.map((file) => (
            <FileRow
              key={file.id}
              file={file}
              bannerError={banner ? sharedError : null}
            />
          ))}
          {moreCount > 0 && (
            <div className="py-1 text-xs text-gray-500">+{moreCount} more</div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuoteUploadPanel;
